import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { Player } from "../interfaces/Team";
import { LegCell, PlayerCell } from "./Cells";
import DoublesTableBody from "./matchDetails/DoublesTableBody";
import { useMatchDetailsContext } from "./matchDetails/MatchDetailsContext";
import "./matchDetails/no-spinner.css";
import SinglesTableBody from "./matchDetails/SinglesTableBody";
import { usePlayersByMatch } from "./matchDetails/usePlayersByMatch";

interface MatchFormProps {
  matchId: number | null;
  homeInputs: { [matchId: number]: string[] };
  awayInputs: { [matchId: number]: string[] };
  setHomeInputs: React.Dispatch<
    React.SetStateAction<{ [matchId: number]: string[] }>
  >;
  setAwayInputs: React.Dispatch<
    React.SetStateAction<{ [matchId: number]: string[] }>
  >;
  loading?: boolean;
  isAdmin: boolean;
}

const MatchForm: React.FC<MatchFormProps> = ({
  matchId,
  homeInputs,
  awayInputs,
  setHomeInputs,
  setAwayInputs,
  isAdmin,
  // loading = false,
}) => {
  // Use custom hooks for data fetching
  const {
    playerMatches,
    playerDoubleMatches,
    // error,
    setPlayerMatches,
    setPlayerDoubleMatches,
    mistakes,
    matchData,
    setMatchData,
  } = useMatchDetailsContext();
  const { homePlayers, awayPlayers } = usePlayersByMatch(matchId || -1);

  const [hoveredSingleRow, setHoveredSingleRow] = useState<number | null>(null);
  const [hoveredDoubleRow, setHoveredDoubleRow] = useState<number | null>(null);

  // Handler for input change
  const handleInputChange = (
    type: "home" | "away",
    matchId: number,
    idx: number,
    value: string
  ) => {
    if (type === "home") {
      setHomeInputs((prev) => {
        const arr = prev[matchId] ? [...prev[matchId]] : ["", "", ""];
        arr[idx] = value;
        return { ...prev, [matchId]: arr };
      });
    } else {
      setAwayInputs((prev) => {
        const arr = prev[matchId] ? [...prev[matchId]] : ["", "", ""];
        arr[idx] = value;
        return { ...prev, [matchId]: arr };
      });
    }
  };

  // Handler for onBlur (simulate API call)
  const handleInputBlur = () => {
    // No-op, kept for possible future use
  };

  // Handler for single matches
  const handleSinglePlayerChange = (
    matchId: number,
    type: "home" | "away",
    playerId: number
  ) => {
    setPlayerMatches((prev) =>
      prev.map((pm) =>
        pm.id === matchId
          ? {
              ...pm,
              [type === "home" ? "home_player" : "away_player"]:
                (type === "home"
                  ? homePlayers.find((p) => p.id === playerId)
                  : awayPlayers.find((p) => p.id === playerId)) || null,
            }
          : pm
      )
    );
  };

  // Handler for double matches
  const handleDoublePlayerChange = (
    matchId: number,
    type: "home1" | "home2" | "away1" | "away2",
    playerId: number
  ) => {
    setPlayerDoubleMatches((prev) =>
      prev.map((dm) => {
        if (dm.id !== matchId) return dm;
        let key: keyof typeof dm;
        let player: Player | null = null;
        switch (type) {
          case "home1":
            key = "home_player_1";
            player = homePlayers.find((p) => p.id === playerId) || null;
            break;
          case "home2":
            key = "home_player_2";
            player = homePlayers.find((p) => p.id === playerId) || null;
            break;
          case "away1":
            key = "away_player_1";
            player = awayPlayers.find((p) => p.id === playerId) || null;
            break;
          case "away2":
            key = "away_player_2";
            player = awayPlayers.find((p) => p.id === playerId) || null;
            break;
          default:
            return dm;
        }
        return { ...dm, [key]: player };
      })
    );
  };

  // Determine admin base URL
  // const adminBaseUrl =
  //   process.env.NODE_ENV === "development"
  //     ? "http://localhost:8000"
  //     : "https://api.wbl-dartliga.de";

  // Memoize derived values for selected player IDs
  const usedHomeSingleIds = React.useMemo(
    () =>
      playerMatches.map((pm) => pm.home_player?.id).filter(Boolean) as number[],
    [playerMatches]
  );
  const usedAwaySingleIds = React.useMemo(
    () =>
      playerMatches.map((pm) => pm.away_player?.id).filter(Boolean) as number[],
    [playerMatches]
  );
  const usedHomeDoubleIds = React.useMemo(
    () =>
      playerDoubleMatches
        .flatMap((dm) => [dm.home_player_1?.id, dm.home_player_2?.id])
        .filter(Boolean) as number[],
    [playerDoubleMatches]
  );
  const usedAwayDoubleIds = React.useMemo(
    () =>
      playerDoubleMatches
        .flatMap((dm) => [dm.away_player_1?.id, dm.away_player_2?.id])
        .filter(Boolean) as number[],
    [playerDoubleMatches]
  );

  return (
    <Box sx={{ mt: 2, position: "relative" }}>
      <Typography variant="h5" sx={{ fontSize: { xs: 18, sm: 24 } }}>
        Einzelspiele
      </Typography>
      {playerMatches.length === 0 ? (
        <Typography variant="body2">Keine Einzelspiele vorhanden.</Typography>
      ) : (
        <Box sx={{ overflowX: "auto", mb: 2 }}>
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{ background: "#f5f5f5", borderBottom: "2px solid #000" }}
              >
                {/* {isAdmin && <HighlightCell></HighlightCell>} */}
                <PlayerCell>Heim</PlayerCell>
                <LegCell hasBorderRight>Legs</LegCell>
                <LegCell>Legs</LegCell>
                <PlayerCell>Gast</PlayerCell>

                {/* {isAdmin && <HighlightCell></HighlightCell>} */}
              </TableRow>
            </TableHead>
            <SinglesTableBody
              playerMatches={playerMatches}
              homePlayers={homePlayers}
              awayPlayers={awayPlayers}
              // Only disable a player if they are already selected in ANOTHER singles match (not doubles)
              usedHomeSingleIds={usedHomeSingleIds}
              usedAwaySingleIds={usedAwaySingleIds}
              homeInputs={homeInputs}
              awayInputs={awayInputs}
              handleSinglePlayerChange={handleSinglePlayerChange}
              handleInputChange={handleInputChange}
              handleInputBlur={handleInputBlur}
              hoveredRow={hoveredSingleRow}
              setHoveredRow={setHoveredSingleRow}
              isAdmin={isAdmin}
            />
          </Table>
        </Box>
      )}
      <Typography variant="h5" sx={{ fontSize: { xs: 18, sm: 24 } }}>
        Doppelspiele
      </Typography>
      {playerDoubleMatches.length === 0 ? (
        <Typography variant="body2">Keine Doppelspiele vorhanden.</Typography>
      ) : (
        <Box sx={{ overflowX: "auto", mb: 2 }}>
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{ background: "#f5f5f5", borderBottom: "2px solid #000" }}
              >
                {/* {isAdmin && <HighlightCell></HighlightCell>} */}
                <PlayerCell>Heim</PlayerCell>
                <LegCell hasBorderRight>Legs</LegCell>
                <LegCell>Legs</LegCell>
                <PlayerCell>Gast</PlayerCell>

                {/* {isAdmin && <HighlightCell></HighlightCell>} */}
              </TableRow>
            </TableHead>
            <DoublesTableBody
              playerDoubleMatches={playerDoubleMatches}
              playerMatchesLength={playerMatches.length}
              homePlayers={homePlayers}
              awayPlayers={awayPlayers}
              // Only disable a player if they are already selected in ANOTHER doubles match (not singles)
              usedHomeDoubleIds={usedHomeDoubleIds}
              usedAwayDoubleIds={usedAwayDoubleIds}
              homeInputs={homeInputs}
              awayInputs={awayInputs}
              handleDoublePlayerChange={handleDoublePlayerChange}
              handleInputChange={handleInputChange}
              handleInputBlur={handleInputBlur}
              hoveredRow={hoveredDoubleRow}
              setHoveredRow={setHoveredDoubleRow}
              isAdmin={isAdmin}
            />
          </Table>
        </Box>
      )}
      {/* Mannschaftsspiel Table if both scores are 3 */}
      {matchData?.home_score === 3 &&
        matchData?.away_score === 3 &&
        !mistakes.length && (
          <Box sx={{ overflowX: "auto", mb: 2 }}>
            <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
              Mannschaftsspiel
            </Typography>
            <Table sx={{ minWidth: 400 }}>
              <TableHead>
                <TableRow sx={{ background: "#f5f5f5" }}>
                  <PlayerCell>Heim</PlayerCell>
                  <PlayerCell>Gast</PlayerCell>
                </TableRow>
              </TableHead>
              <tbody>
                <tr>
                  <td style={{ textAlign: "center", padding: 12 }}>
                    <input
                      type="number"
                      min={0}
                      max={2}
                      value={matchData.decider_legs_home ?? 0}
                      onChange={(e) => {
                        const val =
                          e.target.value === "" ? 0 : Number(e.target.value);
                        setMatchData((prev) =>
                          prev ? { ...prev, decider_legs_home: val } : prev
                        );
                      }}
                      style={{ width: 80, fontSize: 18, textAlign: "center" }}
                    />
                  </td>
                  <td style={{ textAlign: "center", padding: 12 }}>
                    <input
                      type="number"
                      min={0}
                      max={2}
                      value={matchData.decider_legs_away ?? 0}
                      onChange={(e) => {
                        const val =
                          e.target.value === "" ? 0 : Number(e.target.value);
                        setMatchData((prev) =>
                          prev ? { ...prev, decider_legs_away: val } : prev
                        );
                      }}
                      style={{ width: 80, fontSize: 18, textAlign: "center" }}
                    />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Box>
        )}

      {mistakes.length > 0 && (
        <Box sx={{ width: "100%", minHeight: 40 }}>
          <Box
            sx={{
              background: "#ffebee",
              color: "#b71c1c",
              fontWeight: 700,
              fontSize: 18,
              borderRadius: 2,
              border: "1.5px solid #b71c1c",
              boxShadow: "0 2px 12px 0 rgba(183,28,28,0.08)",
              textAlign: "center",
              p: 2,
              mt: 2,
              mb: 2,
            }}
          >
            {mistakes.map((m) => (
              <div key={m.id} style={{ marginBottom: 4 }}>
                {m.error}
              </div>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MatchForm;
